<template>
  <!-- Audio dropped anywhere on the page (Liam, 2026-11-03): into your
       sample library, or into the open session when you're in it. The
       browser no longer opens the file in a tab. Places that take drops
       themselves (the Sessions list starts a session, a session's drop
       row) handle theirs first, and this stands aside. -->
  <div v-if="hint" class="pd-hint" role="status" aria-live="polite">
    <span>{{ hint }}</span>
  </div>
</template>

<script>
import processFiles from "@/mixins/processFiles.js";
import { carriesFiles, isAudio } from "@/components/sessions/dropFiles.js";
import {
  ensureLibrary,
  forgetLibrary,
  isLibraryName,
} from "@/kit/sampleLibrary.js";
import sessionLists from "@/stores/sessionLists.js";
import { toast } from "@/shell/toast.js";

export default {
  name: "PageDrop",
  mixins: [processFiles],
  data: () => ({
    // processFiles uploads to this.sessionName.
    sessionName: null,
    over: false,
    libraryTitle: "",
  }),
  computed: {
    me() {
      return this.$store.state.loggedInUser?.username || null;
    },
    // The open session, when you can add to it (you're in it).
    openSession() {
      const s = this.$store.state.selectedSession;
      if (this.$route?.name !== "Session" || !s || !this.me) return null;
      const members = Array.isArray(s.users) ? s.users : [];
      return s.creator === this.me || members.includes(this.me) ? s : null;
    },
    hint() {
      if (!this.over) return "";
      if (!this.me) return "Log in to add sounds";
      if (this.openSession) return "Drop to add to this session";
      return `Drop to add to ${this.libraryTitle || "your samples"}`;
    },
  },
  watch: {
    me(now, before) {
      if (now !== before) forgetLibrary();
    },
  },
  mounted() {
    window.addEventListener("dragover", this.onDragOver);
    window.addEventListener("dragleave", this.onDragLeave);
    window.addEventListener("drop", this.onDrop);
  },
  beforeUnmount() {
    window.removeEventListener("dragover", this.onDragOver);
    window.removeEventListener("dragleave", this.onDragLeave);
    window.removeEventListener("drop", this.onDrop);
  },
  methods: {
    onDragOver(e) {
      const dt = e.dataTransfer;
      if (!carriesFiles(dt)) return;
      // Ours to take unless a drop target under the pointer already took it.
      if (e.defaultPrevented) {
        this.over = false;
        return;
      }
      e.preventDefault();
      if (dt) dt.dropEffect = this.me ? "copy" : "none";
      this.over = true;
      if (this.me && !this.libraryTitle && !this.openSession) {
        ensureLibrary(this.me)
          .then((lib) => (this.libraryTitle = lib.title))
          .catch(() => {});
      }
    },
    onDragLeave(e) {
      // Leaving the window (no element to go to), not moving between elements.
      if (!e.relatedTarget) this.over = false;
    },
    async onDrop(e) {
      this.over = false;
      const dt = e.dataTransfer;
      if (!carriesFiles(dt) || e.defaultPrevented) return;
      e.preventDefault();
      if (!this.me) {
        toast("Log in to add sounds.");
        return;
      }
      const files = Array.from(dt.files || []).filter(isAudio);
      if (!files.length) {
        toast("Drop WAV, AIFF or FLAC files.");
        return;
      }
      let target = this.openSession ? this.openSession.name : null;
      let where = "this session";
      if (!target) {
        try {
          const lib = await ensureLibrary(this.me);
          target = lib.name;
          where = lib.title;
          this.libraryTitle = lib.title;
        } catch (err) {
          toast("Couldn't reach your samples. Try again.");
          return;
        }
      }
      this.sessionName = target;
      try {
        await this.processFiles(files);
      } catch (err) {
        toast(String(err));
        return;
      }
      const n = files.length;
      const what = n === 1 ? "1 sound" : `${n} sounds`;
      const open = isLibraryName(target)
        ? {
            label: "Open",
            onClick: () =>
              this.$router
                .push(`/session/${encodeURIComponent(target)}`)
                .catch(() => {}),
          }
        : null;
      toast(`Adding ${what} to ${where}.`, { action: open });
    },
    // processFiles calls this after each upload: the library's row on Mine
    // picks up its new sounds.
    loadSession(name) {
      if (isLibraryName(name)) sessionLists.load("mine", { reset: true });
    },
  },
};
</script>

<style scoped>
.pd-hint {
  position: fixed;
  left: 50%;
  bottom: calc(24px + var(--cl-player-h, 0px));
  transform: translateX(-50%);
  z-index: 60;
  pointer-events: none;
}
/* The Sessions pane's drop pill (SessionsPane .sp-drop). */
.pd-hint span {
  display: inline-block;
  padding: 10px 16px;
  border-radius: 14px;
  background: var(--cl-toast);
  border: 1px solid var(--cl-accent-chip-border);
  color: var(--cl-label);
  font: 600 14px/18px var(--cl-font);
}
</style>
